const faq = (q, a) => ({ q, a });

export const CATEGORY_PROCESS_TAX = [
  "Initial discussion to understand your requirement",
  "Document and information checklist shared with you",
  "Preparation, reconciliation and review of the work",
  "Filing / submission and confirmation with acknowledgement",
];

export const CATEGORY_PROCESS_AUDIT = [
  "Scope discussion and engagement confirmation",
  "Information and records checklist shared",
  "Fieldwork, verification and queries",
  "Draft report discussion and final report issuance",
];

export const TAX_CATEGORIES = [
  {
    slug: "direct-taxation",
    title: "Direct Taxation",
    intro:
      "Income tax compliance, planning and representation services for individuals, professionals and businesses.",
    process: CATEGORY_PROCESS_TAX,
    services: [
      {
        slug: "income-tax-return-filing",
        title: "Income Tax Return Filing",
        desc: "Preparation and filing of income tax returns for salaried individuals, professionals, firms and companies, with accurate disclosure and reconciliation of Form 26AS / AIS.",
        areas: ["ITR selection and preparation", "26AS / AIS / TIS reconciliation", "Deductions and exemption review", "Refund tracking support"],
        relevantFor: "Salaried individuals, professionals, business owners, firms and companies.",
        documents: ["PAN and Aadhaar", "Form 16 / income details", "Bank statements and investment proofs"],
        faqs: [
          faq("Which ITR form applies to me?", "The applicable form depends on your sources of income. We evaluate your income profile and file using the correct form."),
          faq("What if I miss the due date?", "A belated return can generally be filed within the permitted period, subject to late fees and interest where applicable."),
        ],
      },
      {
        slug: "tax-planning-advisory",
        title: "Tax Planning & Advisory",
        desc: "Structured, lawful tax planning aligned to current provisions, helping you organise income, investments and transactions efficiently.",
        areas: ["Regime comparison (old vs new)", "Deduction and exemption planning", "Transaction structuring", "Advance tax estimation"],
        relevantFor: "Individuals, professionals and businesses seeking organised, compliant tax planning.",
        documents: ["Income details", "Existing investment information", "Prior year return (if available)"],
        faqs: [
          faq("Is tax planning legal?", "Yes. Tax planning uses deductions, exemptions and structures provided under the law. We do not advise evasion of any kind."),
          faq("When should I start planning?", "Ideally at the beginning of the financial year, though a review can be done at any point."),
        ],
      },
      {
        slug: "tax-representation",
        title: "Tax Representation",
        desc: "Professional representation before income tax authorities for assessments, scrutiny and related proceedings.",
        areas: ["Assessment proceedings", "Response drafting and submissions", "Hearing representation", "Order review and next steps"],
        relevantFor: "Taxpayers facing scrutiny, assessment or reassessment proceedings.",
        documents: ["Notice received", "Returns filed", "Relevant financial records"],
        faqs: [
          faq("Can a CA represent me before the Income Tax Department?", "Yes, a Chartered Accountant can appear as an authorised representative in income tax proceedings."),
          faq("Do I need to attend personally?", "In most proceedings your representative can attend on your behalf; personal appearance is rarely required unless specifically directed."),
        ],
      },
      {
        slug: "tax-notices-compliance",
        title: "Tax Notices & Compliance",
        desc: "Evaluation of and response to income tax notices, including defective returns, mismatch communications and compliance portal items.",
        areas: ["Notice evaluation", "Response preparation and filing", "e-Proceedings portal handling", "Rectification requests"],
        relevantFor: "Anyone who has received an income tax notice or compliance communication.",
        documents: ["Copy of the notice", "Relevant ITR and computation", "Supporting records"],
        faqs: [
          faq("Should I ignore a tax notice?", "No. Every notice should be reviewed and responded to within the stipulated time to avoid adverse consequences."),
          faq("How quickly must I respond?", "Timelines are mentioned in the notice itself. Share the notice with us as early as possible."),
        ],
      },
      {
        slug: "tds-tcs-compliance",
        title: "TDS / TCS Compliance",
        desc: "End-to-end TDS and TCS compliance: deduction advice, payment, quarterly returns, corrections and certificate issuance.",
        areas: ["TDS applicability advisory", "Quarterly return filing (24Q, 26Q etc.)", "TRACES corrections", "Form 16 / 16A issuance"],
        relevantFor: "Businesses and individuals responsible for deducting or collecting tax at source.",
        documents: ["TAN details", "Deductee payment records", "Previous returns (if any)"],
        faqs: [
          faq("What happens if TDS returns are filed late?", "Late filing fees and interest apply under the Income-tax Act, so timely filing is important."),
          faq("Can past errors be corrected?", "Yes, correction statements can be filed on TRACES to rectify most errors."),
        ],
      },
      {
        slug: "capital-gains-tax",
        title: "Capital Gains Tax",
        desc: "Computation and reporting of capital gains on shares, mutual funds, property and other assets, including exemptions where applicable.",
        areas: ["Gain computation and classification", "Property sale tax workings", "Exemption advisory (e.g. Sections 54 series)", "Reporting in ITR"],
        relevantFor: "Investors and property sellers with capital gains or losses.",
        documents: ["Purchase and sale documents", "Broker statements", "Improvement cost records (property)"],
        faqs: [
          faq("How are property gains taxed?", "Gains are classified as short or long term based on holding period and taxed at applicable rates after permissible deductions."),
          faq("Can capital gains tax be saved lawfully?", "Certain exemptions are available on reinvestment under specified conditions; eligibility must be evaluated case by case."),
        ],
      },
      {
        slug: "business-professional-taxation",
        title: "Business & Professional Taxation",
        desc: "Tax computation, presumptive scheme evaluation and return filing for businesses, firms, LLPs and professionals.",
        areas: ["Business income computation", "Presumptive taxation evaluation", "Firm / LLP taxation", "Advance tax planning"],
        relevantFor: "Proprietors, partnerships, LLPs, companies and independent professionals.",
        documents: ["Books of account / financials", "Bank statements", "Expense records"],
        faqs: [
          faq("What is presumptive taxation?", "It is a simplified scheme where income is computed at prescribed rates for eligible small businesses and professionals, subject to conditions."),
          faq("Do I need audited accounts?", "Audit applicability depends on turnover and other conditions; we evaluate this for you."),
        ],
      },
      {
        slug: "nri-taxation",
        title: "NRI Taxation",
        desc: "Taxation services for Non-Resident Indians covering residential status, India-sourced income, property transactions and return filing.",
        areas: ["Residential status evaluation", "NRO / NRE interest taxation", "Property sale TDS and lower deduction certificates", "NRI return filing"],
        relevantFor: "NRIs, OCIs and returning Indians with income or assets in India.",
        documents: ["Passport travel details", "Indian income records", "Asset purchase documents"],
        faqs: [
          faq("Do NRIs need to file returns in India?", "If income in India exceeds the basic exemption limit or in other specified cases, filing is required."),
          faq("Can TDS on property sale be reduced?", "A lower deduction certificate may be applied for where actual tax liability is lower than the prescribed TDS."),
        ],
      },
      {
        slug: "international-taxation",
        title: "International Taxation",
        desc: "Advisory on cross-border tax matters including DTAA relief, foreign income reporting and residency-related positions.",
        areas: ["DTAA analysis", "Foreign income and asset reporting", "Cross-border transaction taxation", "Residency advisory"],
        relevantFor: "Individuals and businesses with cross-border income, assets or transactions.",
        documents: ["Foreign income details", "Tax residency certificates", "Relevant agreements"],
        faqs: [
          faq("What is DTAA?", "Double Taxation Avoidance Agreements allocate taxing rights between countries and can provide relief from double taxation."),
          faq("Must foreign assets be reported?", "Residents are generally required to disclose foreign assets and income in their Indian return."),
        ],
      },
    ],
  },
  {
    slug: "gst-indirect-taxation",
    title: "GST & Indirect Taxation",
    intro:
      "Goods and Services Tax registration, periodic compliance, advisory, reconciliation and representation services.",
    process: CATEGORY_PROCESS_TAX,
    services: [
      {
        slug: "gst-registration",
        title: "GST Registration",
        desc: "New GST registration for businesses and professionals, including document preparation, application filing and follow-up with the department.",
        areas: ["Eligibility and scheme evaluation", "Application preparation and filing", "Clarification handling", "Post-registration guidance"],
        relevantFor: "New businesses, e-commerce sellers and those crossing the registration threshold.",
        documents: ["PAN and Aadhaar", "Address proof of business place", "Bank details and photographs"],
        faqs: [
          faq("Who must register under GST?", "Registration is required once turnover crosses the prescribed threshold, and earlier in specified cases such as inter-state supply or e-commerce."),
          faq("How long does registration take?", "Typically a few working days after a complete application, subject to departmental processing."),
        ],
      },
      {
        slug: "gst-return-compliance",
        title: "GST Return Compliance",
        desc: "Periodic filing of GSTR-1, GSTR-3B and other applicable returns with reconciliation of sales and input tax credit.",
        areas: ["GSTR-1 and GSTR-3B filing", "QRMP scheme handling", "ITC reconciliation", "Nil and late return filing"],
        relevantFor: "All GST-registered businesses and professionals.",
        documents: ["Sales and purchase data", "Bank / payment summaries", "GSTR-2B review access"],
        faqs: [
          faq("What happens if GST returns are not filed?", "Late fees and interest apply, and continued non-filing can block further filing and e-way bill generation."),
          faq("Can you handle filings monthly?", "Yes, we manage periodic compliance on a recurring basis."),
        ],
      },
      {
        slug: "gst-advisory",
        title: "GST Advisory",
        desc: "Opinions and guidance on GST classification, rates, place of supply, input credit and transaction structuring.",
        areas: ["Classification and rate analysis", "Place and time of supply", "ITC eligibility review", "Contract and invoice structuring inputs"],
        relevantFor: "Businesses entering new transactions, products or markets.",
        documents: ["Transaction details", "Contracts / invoices", "Product or service descriptions"],
        faqs: [
          faq("Why take advice before a transaction?", "GST positions are hard to correct after the fact; advance clarity avoids disputes and credit loss."),
          faq("Do you give written opinions?", "Yes, documented opinions can be provided based on facts shared."),
        ],
      },
      {
        slug: "gst-reconciliation",
        title: "GST Reconciliation",
        desc: "Systematic reconciliation of books, GSTR-1, GSTR-3B and GSTR-2B to identify mismatches and protect input tax credit.",
        areas: ["Books vs returns matching", "GSTR-2B vs purchase register", "Vendor follow-up support", "Mismatch reporting"],
        relevantFor: "Businesses with significant transaction volumes or credit mismatches.",
        documents: ["Books of account", "GST portal data", "Purchase register"],
        faqs: [
          faq("Why is reconciliation important?", "Unmatched invoices directly affect input tax credit and can trigger departmental communications."),
          faq("How often should it be done?", "Monthly reconciliation is ideal; at minimum, it should be done before annual filings."),
        ],
      },
      {
        slug: "gst-annual-compliance",
        title: "GST Annual Compliance",
        desc: "Preparation and filing of annual GST returns (GSTR-9) and reconciliation statements (GSTR-9C) where applicable.",
        areas: ["GSTR-9 preparation", "GSTR-9C reconciliation and certification", "Annual ITC review", "Disclosure clean-up"],
        relevantFor: "Registered taxpayers meeting annual return applicability criteria.",
        documents: ["Monthly return data", "Audited / final books", "ITC registers"],
        faqs: [
          faq("Who must file GSTR-9?", "Applicability depends on registration type and turnover thresholds notified for the year."),
          faq("When is it due?", "Generally by 31 December following the financial year, unless extended by notification."),
        ],
      },
      {
        slug: "gst-refund-assistance",
        title: "GST Refund Assistance",
        desc: "Preparation and pursuit of GST refund claims including exports under LUT, inverted duty structure and excess balance cases.",
        areas: ["Refund eligibility evaluation", "Application preparation (RFD-01)", "Departmental follow-up", "Deficiency response"],
        relevantFor: "Exporters, businesses with inverted duty structure or excess cash ledger balances.",
        documents: ["Returns filed", "Export / supply documents", "Bank and ledger details"],
        faqs: [
          faq("How long do GST refunds take?", "Statutory timelines exist, but practical timelines vary with verification and completeness of the claim."),
          faq("Can LUT-based exporters claim refunds?", "Yes, refund of unutilised input tax credit can be claimed for zero-rated supplies, subject to conditions."),
        ],
      },
      {
        slug: "gst-notices-representation",
        title: "GST Notices & Representation",
        desc: "Evaluation and response to GST notices, summons and assessment proceedings, with representation before authorities.",
        areas: ["Notice evaluation and strategy", "Reply drafting and filing", "Personal hearing representation", "Appeal stage advisory"],
        relevantFor: "Taxpayers who have received GST notices, show cause communications or audit intimations.",
        documents: ["Notice copy", "Returns and reconciliation", "Relevant records"],
        faqs: [
          faq("Is every GST notice serious?", "Notices vary in nature - from simple clarifications to demand proceedings. Each should be evaluated promptly."),
          faq("Can a CA appear in GST proceedings?", "Yes, Chartered Accountants are authorised representatives under GST law."),
        ],
      },
      {
        slug: "e-invoicing-eway-bill-advisory",
        title: "E-invoicing / E-way Bill Advisory",
        desc: "Implementation and compliance support for e-invoicing and e-way bill requirements, including system and process alignment.",
        areas: ["Applicability evaluation", "Implementation support", "Process and training guidance", "Error and cancellation handling"],
        relevantFor: "Businesses crossing e-invoicing thresholds or transporting goods.",
        documents: ["Turnover details", "Invoice formats", "ERP / billing software information"],
        faqs: [
          faq("Who must generate e-invoices?", "Registered persons whose aggregate turnover crosses the notified threshold, currently applied in phases."),
          faq("Is an e-way bill needed for every movement?", "Generally for movement of goods above the prescribed value, subject to rules and exceptions."),
        ],
      },
    ],
  },
  {
    slug: "audit-assurance",
    title: "Audit & Assurance",
    intro:
      "Statutory, tax, internal, bank and special-purpose audits conducted with professional rigour and clear reporting.",
    process: CATEGORY_PROCESS_AUDIT,
    services: [
      {
        slug: "statutory-audit",
        title: "Statutory Audit",
        desc: "Independent audit of financial statements as required under the Companies Act or other applicable statutes, with clear reporting.",
        areas: ["Financial statement audit", "Internal control observations", "CARO reporting (companies)", "Audit report issuance"],
        relevantFor: "Companies, LLPs and entities subject to statutory audit requirements.",
        documents: ["Trial balance and books", "Bank statements and confirmations", "Statutory registers and agreements"],
        faqs: [
          faq("Who needs a statutory audit?", "All companies require an annual statutory audit; other entities depend on applicable law and thresholds."),
          faq("How long does an audit take?", "It depends on size and record readiness; early preparation significantly shortens timelines."),
        ],
      },
      {
        slug: "tax-audit",
        title: "Tax Audit",
        desc: "Audit under Section 44AB of the Income-tax Act with reporting in the prescribed forms, completed well before due dates.",
        areas: ["Applicability evaluation", "Form 3CA / 3CB and 3CD reporting", "Clause-wise verification", "Filing of audit report"],
        relevantFor: "Businesses and professionals crossing prescribed turnover or receipt thresholds.",
        documents: ["Books of account", "GST returns", "TDS returns and expense records"],
        faqs: [
          faq("When is a tax audit required?", "When turnover or gross receipts exceed prescribed limits, subject to conditions including cash transaction thresholds."),
          faq("What is the due date?", "Generally one month before the return filing due date for audit cases, unless extended."),
        ],
      },
      {
        slug: "internal-audit",
        title: "Internal Audit",
        desc: "Risk-oriented internal audits that evaluate processes, controls and compliance, with actionable observations for management.",
        areas: ["Process and control review", "Compliance verification", "Fraud risk indicators", "Management reporting"],
        relevantFor: "Companies with internal audit applicability and businesses seeking stronger controls.",
        documents: ["Process documents", "Books and records access", "Prior audit reports"],
        faqs: [
          faq("Is internal audit mandatory?", "For specified classes of companies, yes. Others engage internal audit voluntarily to strengthen controls."),
          faq("How is it different from statutory audit?", "Internal audit focuses on processes and controls for management; statutory audit reports on financial statements to stakeholders."),
        ],
      },
      {
        slug: "bank-audit",
        title: "Bank Audit",
        desc: "Branch statutory audits of banks conducted as per RBI guidelines and the appointed scope, within stipulated timelines.",
        areas: ["Branch statutory audit", "Advances and NPA verification", "LFAR reporting", "Compliance verification"],
        relevantFor: "Bank branches allotted through the appointment process.",
        documents: ["As per appointment letter scope", "Branch records", "System reports"],
        faqs: [
          faq("How are bank audits allotted?", "Through the bank's appointment process for empanelled auditors."),
          faq("What is LFAR?", "The Long Form Audit Report - a detailed questionnaire-based report submitted along with the branch audit."),
        ],
      },
      {
        slug: "concurrent-audit",
        title: "Concurrent Audit",
        desc: "Ongoing, periodic audit of bank branch transactions conducted concurrently with operations, as per RBI norms.",
        areas: ["Monthly transaction coverage", "Advances documentation checks", "Regulatory compliance checks", "Periodic reporting"],
        relevantFor: "Bank branches under concurrent audit requirements.",
        documents: ["Scope from appointing bank", "Branch system access", "Monthly MIS"],
        faqs: [
          faq("What is the coverage norm?", "Coverage expectations are defined by the bank's policy and RBI guidelines, typically covering a substantial share of transactions."),
          faq("How frequently is reporting done?", "Usually monthly, as per the appointment terms."),
        ],
      },
      {
        slug: "stock-process-audit",
        title: "Stock / Process Audit",
        desc: "Independent verification of stock, receivables and operational processes, commonly required by lenders and management.",
        areas: ["Physical stock verification", "Receivable and debtor review", "Drawing power support", "Process gap reporting"],
        relevantFor: "Businesses with working capital limits and lenders requiring independent verification.",
        documents: ["Stock records", "Debtors listing", "Sanction terms"],
        faqs: [
          faq("Why do banks ask for stock audits?", "Lenders use independent verification to confirm the security underlying working capital finance."),
          faq("How often is it conducted?", "Frequency follows the sanction terms - commonly quarterly, half-yearly or annually."),
        ],
      },
      {
        slug: "due-diligence",
        title: "Due Diligence",
        desc: "Financial and tax due diligence for acquisitions, investments and lending decisions, highlighting risks and exposures.",
        areas: ["Financial records review", "Tax compliance verification", "Contingent liability identification", "Diligence reporting"],
        relevantFor: "Investors, acquirers, lenders and business sellers.",
        documents: ["Financial statements", "Tax filings and assessments", "Key contracts"],
        faqs: [
          faq("What does due diligence cover?", "Scope is tailored - commonly financial, tax and compliance areas relevant to the transaction."),
          faq("How long does it take?", "Typically a few weeks depending on record availability and scope."),
        ],
      },
    ],
  },
];
